import React, { useState } from 'react';
import ActionsArrowDownIcon from 'client/component-library/fds/icons/react/ActionsArrowDownIcon';
import ActionsArrowRightIcon from 'client/component-library/fds/icons/react/ActionsArrowRightIcon';
import Button from 'client/component-library/buttons/ButtonAdmin/afsButton';
import { Card } from 'client/component-library/Cards/Card';
import { EntitySearch } from 'client/modules/cbi-entity/components/cbi-entity/EntitySearch';
import ExternalLinkIcon from 'client/component-library/fds/icons/react/ExternalLinkIcon';
import { DealActionButtons } from './DealActionButtons';
import { DealUpdateMenu } from './DealUpdateMenu';
import {
  DealCard as DealCardModel,
  DealExtraction,
  PotentialDuplicateDraft,
  PotentialDuplicateFunding,
} from '../apis/GetDealQueue';
import { dealTypeLabel } from '../utils/dealConstants';
import { UpdateMergeOptions, UpdateTarget } from '../utils/dealUpdate';

interface DealCardProps extends DealCardModel {
  onPublish: (card: DealCardModel) => void;
  onReject: (card: DealCardModel) => void;
  onSkip: (card: DealCardModel) => void;
  // Merge this deal into an existing funding/draft instead of creating a new draft (see DealUpdateMenu).
  onUpdate?: (
    card: DealCardModel,
    target: UpdateTarget,
    merge: UpdateMergeOptions
  ) => void;
}

// Lets the reviewer override the anchor's entity for the Publish handoff only (frontend-only; not
// persisted). Passed to the anchor's ExtractionFields; member rows don't get it.
interface EntityEditControls {
  override: { idCbiEntity: number; name: string } | null;
  editing: boolean;
  setEditing: (editing: boolean) => void;
  onSelect: (idCbiEntity: number, name: string) => void;
  onCreate: (name: string) => void;
}

// created_at arrives as "YYYY-MM-DD HH:MM:SS"; show date + HH:MM, dropping seconds.
function formatExtractedAt(raw: string): string {
  return raw.length >= 16 ? raw.slice(0, 16) : raw;
}

// Amount display. Normal case: `amount` is the converted USD (millions) value -- shown plainly (the
// column header says USD/millions). Edge case: when USD conversion failed, `amount` is null but
// `amountRaw` is populated -- show the raw pre-conversion amount + currency code, highlighted light
// yellow as a warning. The raw value is NOT carried into the funding form on Publish (see
// dealToFundingRow, which only reads `amount`), so the reviewer converts it manually.
const AmountValue = ({ extraction }: { extraction: DealExtraction }) => {
  if (extraction.amount !== null && extraction.amount !== undefined) {
    return <span>{extraction.amount}</span>;
  }
  if (extraction.amountRaw !== null && extraction.amountRaw !== undefined) {
    return (
      <span
        className="bg-yellow-100 px-1 rounded"
        title="USD conversion unavailable — this is the raw extracted amount. Convert to USD (millions) manually before publishing."
      >
        {extraction.amountRaw}
        {extraction.amountCurrency ? ` ${extraction.amountCurrency}` : ''}
      </span>
    );
  }
  return <span>—</span>;
};

// A labeled column, mirroring the funding-tab field layout.
const FieldCol = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col" style={{ minWidth: 110 }}>
    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
      {label}
    </span>
    <span className="text-sm text-gray-800">{children}</span>
  </div>
);

const entityAdminUrl = (idCbiEntity: number) =>
  `/cbi-entity/${idCbiEntity}/general-info`;

// The entity's fundings tab; `funding` / `draft` make the page scroll to and highlight that row.
export const entityFundingsUrl = (
  idCbiEntity: number,
  focus: { funding?: number; draft?: string } = {}
) => {
  const params = new URLSearchParams();
  if (focus.draft) {
    params.set('draft', focus.draft);
  } else if (focus.funding) {
    params.set('funding', String(focus.funding));
  }
  const query = params.toString();
  return `/cbi-entity/${idCbiEntity}/fundings${query ? `?${query}` : ''}`;
};

// "Series A, 10M, 2026-01-15, started 2026-01-17" -- whichever parts of an active draft are known.
const potentialDraftDetail = (draft: PotentialDuplicateDraft) =>
  [
    draft.round,
    draft.amount != null ? `${draft.amount}M` : '',
    draft.fundingDate,
    draft.createdAt ? `started ${draft.createdAt.slice(0, 10)}` : '',
  ]
    .filter(Boolean)
    .join(', ');

// "Series A, 10M, 2026-01-15" -- whichever parts of a potential-duplicate funding are known.
const potentialDuplicateDetail = (dup: PotentialDuplicateFunding) =>
  [dup.round, dup.amount != null ? `${dup.amount}M` : '', dup.fundingDate]
    .filter(Boolean)
    .join(', ');

// Ensure an external URL has a scheme; otherwise the browser treats "volantis.com" as a relative
// path and resolves it against the current origin (e.g. localhost:8007/volantis.com).
const externalHref = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const ExtractionFields = ({
  extraction,
  entityEdit,
}: {
  extraction: DealExtraction;
  entityEdit?: EntityEditControls;
}) => {
  const investors = extraction.investors.filter((i) => i.name);
  // The effective entity for this row: the reviewer's override (anchor only) or the extracted one.
  const entity = entityEdit?.override ?? {
    idCbiEntity: extraction.idCbiEntity,
    name: extraction.companyName,
  };
  const entityName = entity.name || 'Unknown company';
  return (
    <div className="flex flex-col gap-y-2">
      {/* Already-published banner: this deal's cluster is linked to an existing funding. Yellow +
          lists the fields that differ (net-new info) -- or notes it fully matches. */}
      {extraction.publishedFunding && (
        <div
          className="bg-yellow-100 border border-yellow-300 rounded px-2 py-1 text-xs text-gray-800"
          data-test="published-funding-banner"
        >
          <span className="font-semibold">
            Already in our system — published as{' '}
            <a
              className="text-blue-600 underline"
              href={entityFundingsUrl(extraction.idCbiEntity, {
                funding: extraction.publishedFunding.idFunding,
              })}
              target="_blank"
              rel="noreferrer"
            >
              funding #{extraction.publishedFunding.idFunding}
            </a>
          </span>
          {extraction.publishedFunding.differences.length > 0 ? (
            <ul className="mt-1 list-disc list-inside">
              {extraction.publishedFunding.differences.map((d) => (
                <li key={d.field}>
                  <span className="capitalize">{d.field}</span>: extracted{' '}
                  <b>{d.extracted || '—'}</b> vs published{' '}
                  <b>{d.published || '—'}</b>
                </li>
              ))}
            </ul>
          ) : (
            <span className="pl-1">(matches the published funding)</span>
          )}
        </div>
      )}
      {/* Live potential-duplicate warning: fundings for this company were published AFTER this deal was
          extracted and aren't linked to it, so the dupe job couldn't have compared against them. */}
      {extraction.potentialDuplicates.length > 0 && (
        <div
          className="bg-orange-100 border border-orange-300 rounded px-2 py-1 text-xs text-gray-800"
          data-test="potential-duplicate-warning"
        >
          <span className="font-semibold">
            <span role="img" aria-label="warning">
              ⚠
            </span>{' '}
            Possible duplicate —{' '}
            {extraction.potentialDuplicates.length === 1
              ? 'a funding was'
              : 'fundings were'}{' '}
            published for this company after this deal was extracted
          </span>
          <ul className="mt-1 list-disc list-inside">
            {extraction.potentialDuplicates.map((dup) => (
              <li key={dup.idFunding}>
                <a
                  className="text-blue-600 underline"
                  href={entityFundingsUrl(extraction.idCbiEntity, {
                    funding: dup.idFunding,
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  Funding #{dup.idFunding}
                </a>
                {potentialDuplicateDetail(dup) && (
                  <span> ({potentialDuplicateDetail(dup)})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Live active-draft warning: someone already has an unpublished funding draft for this company that
          matches this deal's round or funding month, so there's likely nothing for the reviewer to enter. */}
      {extraction.potentialDuplicateDrafts.length > 0 && (
        <div
          className="bg-orange-100 border border-orange-300 rounded px-2 py-1 text-xs text-gray-800"
          data-test="potential-duplicate-draft-warning"
        >
          <span className="font-semibold">
            <span role="img" aria-label="warning">
              ⚠
            </span>{' '}
            Active funding draft —{' '}
            {extraction.potentialDuplicateDrafts.length === 1
              ? 'a draft'
              : 'drafts'}{' '}
            already in progress for this company
          </span>
          <ul className="mt-1 list-disc list-inside">
            {extraction.potentialDuplicateDrafts.map((draft) => (
              <li key={draft.idFundingDraft}>
                <a
                  className="text-blue-600 underline"
                  href={entityFundingsUrl(extraction.idCbiEntity, {
                    draft: draft.idFundingDraft,
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  {draft.idFunding > 0
                    ? `Draft edit of funding #${draft.idFunding}`
                    : 'New funding draft'}
                </a>
                {potentialDraftDetail(draft) && (
                  <span> ({potentialDraftDetail(draft)})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Fields in funding-tab order: Entity, Date, Round, Amount, Investors */}
      <div className="flex flex-row flex-wrap gap-x-8 gap-y-2">
        <FieldCol label="Entity">
          {entity.idCbiEntity > 0 ? (
            <a
              className="font-bold text-blue-600 underline"
              href={entityAdminUrl(entity.idCbiEntity)}
              target="_blank"
              rel="noreferrer"
            >
              {entityName}
            </a>
          ) : (
            <span className="font-bold">{entityName}</span>
          )}
          {extraction.companyUrl && !entityEdit?.override && (
            <a
              className="pl-1 text-xs text-blue-500 underline"
              href={externalHref(extraction.companyUrl)}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLinkIcon customSize={10} />
            </a>
          )}
          {entityEdit && (
            <button
              type="button"
              className="pl-2 text-xs text-blue-600 underline"
              onClick={() => entityEdit.setEditing(!entityEdit.editing)}
              data-test="edit-entity-toggle"
            >
              {entityEdit.editing ? 'cancel' : 'change'}
            </button>
          )}
          {entityEdit?.override && (
            <span className="pl-1 text-xs text-green-600">(overridden)</span>
          )}
          {entity.idCbiEntity <= 0 && !entityEdit?.editing && (
            <span className="pl-1 text-xs text-red-500">
              {entityEdit
                ? '(not linked — use “change” to pick or create an entity)'
                : '(not linked)'}
            </span>
          )}
          {entityEdit?.editing && (
            <div className="mt-1" style={{ minWidth: 280 }}>
              <EntitySearch
                showCreateEntity
                // Fundings publish only to real entities; hide draft-only results so a draft can't be
                // selected (a no-op) -- reviewers use "Create Entity Draft" for new entities instead.
                searchDrafts={false}
                placeholder="Search for an entity..."
                onSelection={(_value, result) => {
                  if (result?.idCbiEntity) {
                    entityEdit.onSelect(result.idCbiEntity, result.name);
                  }
                }}
                onCreate={(search) => entityEdit.onCreate(search)}
              />
            </div>
          )}
        </FieldCol>
        <FieldCol label="Date">{extraction.dealDate || '—'}</FieldCol>
        <FieldCol label="Round">{extraction.round || '—'}</FieldCol>
        <FieldCol label="Round (raw)">{extraction.roundRaw || '—'}</FieldCol>
        <FieldCol label="Amount (USD M)">
          <AmountValue extraction={extraction} />
        </FieldCol>
        <FieldCol label="Investors">
          {investors.length
            ? investors.map((inv, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={`${inv.idCbiEntity}-${inv.name}-${i}`}>
                  {inv.idCbiEntity > 0 ? (
                    <a
                      className="text-blue-600 underline"
                      href={entityAdminUrl(inv.idCbiEntity)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {inv.name}
                    </a>
                  ) : (
                    <span>{inv.name}</span>
                  )}
                  {i < investors.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))
            : '—'}
        </FieldCol>
      </div>

      {/* Mentions -- always on its own line */}
      <FieldCol label="Mentions">
        {extraction.newsTitle || extraction.newsUrl ? (
          <a
            href={externalHref(extraction.newsUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-blue-500 underline"
          >
            {extraction.newsTitle || extraction.newsUrl}
            <span className="pl-1 inline-block align-middle">
              <ExternalLinkIcon customSize={10} />
            </span>
          </a>
        ) : (
          '—'
        )}
      </FieldCol>

      {/* Source snippet -- the reviewer's best signal, not on the funding tab */}
      {extraction.sentence && (
        <div className="text-xs text-gray-600 italic">
          &ldquo;{extraction.sentence}&rdquo;
        </div>
      )}

      {/* Extraction-only metadata */}
      <div className="flex flex-row flex-wrap gap-x-4 text-xs text-gray-500">
        <span>Type: {dealTypeLabel(extraction.dealType)}</span>
        {extraction.dealStatus && <span>Status: {extraction.dealStatus}</span>}
        <span>{extraction.isMainDeal ? 'Main deal' : 'Secondary deal'}</span>
        {extraction.newsLanguage && (
          <span>Lang: {extraction.newsLanguage}</span>
        )}
        {extraction.newsSourceId > 0 && (
          <span>Source: {extraction.newsSourceId}</span>
        )}
        {extraction.extractedAt && (
          <span>Extracted: {formatExtractedAt(extraction.extractedAt)}</span>
        )}
      </div>
    </div>
  );
};

export const DealCard = (props: DealCardProps) => {
  const {
    idNewsDeal,
    anchor,
    memberDeals,
    onPublish,
    onReject,
    onSkip,
    onUpdate,
  } = props;
  const [showMembers, setShowMembers] = useState(false);

  // Frontend-only entity override for the Publish handoff (not persisted; resets on reload). Lets a
  // reviewer correct an unmapped or wrongly-mapped deal so Publish opens the right entity's fundings.
  const [editingEntity, setEditingEntity] = useState(false);
  const [entityOverride, setEntityOverride] = useState<{
    idCbiEntity: number;
    name: string;
  } | null>(null);

  const card: DealCardModel = { idNewsDeal, anchor, memberDeals };

  const effectiveIdCbiEntity =
    entityOverride?.idCbiEntity ?? anchor.idCbiEntity;
  // Publish (only) with the overridden entity so the funding admin opens the correct /cbi-entity/{id}.
  // Reject/Skip stay on the original `card` so the frontend-only override never leaks into them.
  const publishCard: DealCardModel = entityOverride
    ? {
        ...card,
        anchor: { ...anchor, idCbiEntity: entityOverride.idCbiEntity },
      }
    : card;

  const entityEdit: EntityEditControls = {
    override: entityOverride,
    editing: editingEntity,
    setEditing: setEditingEntity,
    onSelect: (idCbiEntity, name) => {
      setEntityOverride({ idCbiEntity, name });
      setEditingEntity(false);
    },
    onCreate: (name) => {
      window.open(
        `/cbi-entity/new?entityName=${encodeURIComponent(name)}`,
        '_blank'
      );
    },
  };

  return (
    <Card>
      <div className="flex flex-row w-full py-2">
        <div className="w-10/12 px-4 flex flex-col justify-start">
          <div className="text-xs text-gray-400 mb-1" data-test="deal-card-id">
            Deal #{idNewsDeal}
          </div>
          <ExtractionFields extraction={anchor} entityEdit={entityEdit} />
          {memberDeals.length > 0 && (
            <div className="mt-2">
              <Button
                onClick={() => setShowMembers(!showMembers)}
                theme="ghost"
                inline
                data-test="toggle-members"
              >
                {showMembers ? (
                  <ActionsArrowDownIcon customSize={15} />
                ) : (
                  <ActionsArrowRightIcon customSize={15} />
                )}
                <span className="pl-1 text-xs">
                  {memberDeals.length} other{' '}
                  {memberDeals.length > 1 ? 'extractions' : 'extraction'} of
                  this deal
                </span>
              </Button>
              {showMembers && (
                <div className="flex flex-col gap-y-4 mt-2 pl-4 border-l-2 border-gray-200">
                  {memberDeals.map((member) => (
                    <ExtractionFields
                      key={member.idNewsDeal}
                      extraction={member}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DealActionButtons
          onPublish={() => onPublish(publishCard)}
          onReject={() => onReject(card)}
          onSkip={() => onSkip(card)}
          // Publishing needs a resolved entity; idCbiEntity <= 0 would open /cbi-entity/0/fundings
          // (a dead end), so disable until the deal is linked -- either the extracted entity or a
          // reviewer override via "change".
          disablePublish={effectiveIdCbiEntity <= 0}
          updateSlot={
            onUpdate && (
              <DealUpdateMenu
                card={card}
                idCbiEntity={anchor.idCbiEntity}
                onUpdate={(target, merge) => onUpdate(card, target, merge)}
              />
            )
          }
        />
      </div>
    </Card>
  );
};
